/**
 * Workout phases — blocks of continuous swimming split by rest gaps > 25s.
 * Each phase rolls up its lap range, meters, dominant stroke, and avgHr;
 * `labelPhases` then assigns heuristic names (Warm-up, Main block,
 * Set 1 of N, Re-entry, Block N).
 */
import type { Lap } from "./laps";
import { REST_GAP_MS } from "./restConstant";

export interface WorkoutPhase {
  index: number;
  label: string;               // "Warm-up", "Main block", "Set 1 of 3", etc.
  startMs: number;
  endMs: number;
  durationSeconds: number;
  lapStart: number;            // 1-based lap index
  lapEnd: number;
  meters: number;
  dominantStroke: string;      // most-swum stroke in this phase
  avgHr: number | null;
}

export function buildPhases(laps: Lap[], poolLengthMeters: number | null): WorkoutPhase[] {
  const phaseBlocks: Lap[][] = [];
  let current: Lap[] = [];
  let lastEndMs = 0;
  // DRILL-03: excluded laps don't belong to any phase block.
  for (const lap of laps.filter((l) => !l.effectiveExcluded)) {
    if (current.length === 0 || (lap.startMs - lastEndMs) <= REST_GAP_MS) {
      current.push(lap);
    } else {
      if (current.length > 0) phaseBlocks.push(current);
      current = [lap];
    }
    lastEndMs = lap.endMs;
  }
  if (current.length > 0) phaseBlocks.push(current);

  const phases: WorkoutPhase[] = phaseBlocks.map((block, i) => {
    const lapStart = block[0].index;
    const lapEnd = block[block.length - 1].index;
    const startMs = block[0].startMs;
    const endMs = block[block.length - 1].endMs;
    const durationSeconds = (endMs - startMs) / 1000;
    const meters = block.reduce((s, l) => s + (l.lengthMeters ?? poolLengthMeters ?? 0), 0);
    const strokeCounts = new Map<string, number>();
    for (const lap of block) {
      strokeCounts.set(lap.strokeStyle, (strokeCounts.get(lap.strokeStyle) ?? 0) + 1);
    }
    const dominantStroke = [...strokeCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
    const blockHrs = block.map((l) => l.avgHr).filter((h): h is number => h !== null);
    const phAvgHr = blockHrs.length > 0 ? blockHrs.reduce((a, b) => a + b, 0) / blockHrs.length : null;

    return {
      index: i + 1,
      label: "",  // assigned by labelPhases
      startMs,
      endMs,
      durationSeconds,
      lapStart,
      lapEnd,
      meters,
      dominantStroke,
      avgHr: phAvgHr,
    };
  });

  labelPhases(phases);
  return phases;
}

/**
 * Heuristic phase labeling. Rules:
 *   - Phase 1 = "Warm-up" (always)
 *   - The single longest non-first phase = "Main block"
 *   - Short phases (<300m) AFTER the Main block, when there are 2+ of them,
 *     get "Set 1 of N", "Set 2 of N", etc. — regardless of how similar their
 *     durations are. Real workouts have finishers of 2-4 sets with varying
 *     sizes; a prior "must be similar in duration" rule missed Scott's actual
 *     8/8/10-length finisher.
 *   - Anything still unlabeled gets a position-aware fallback ("Re-entry"
 *     for short/quick, "Block N" otherwise).
 *
 * Mutates `phases` in place.
 */
export function labelPhases(phases: WorkoutPhase[]): void {
  if (phases.length === 0) return;
  phases[0].label = "Warm-up";

  const candidates = phases.length >= 3 ? phases.slice(1) : phases.slice();
  if (candidates.length > 0) {
    const mainBlock = candidates.reduce((max, p) => (p.durationSeconds > max.durationSeconds ? p : max));
    const mainBlockIdx = phases.indexOf(mainBlock);
    if (mainBlockIdx > 0) {
      mainBlock.label = "Main block";
      const afterMain = phases.slice(mainBlockIdx + 1);
      const shortAfter = afterMain.filter((p) => p.meters < 300);
      if (shortAfter.length >= 2) {
        let setNum = 0;
        for (const p of afterMain) {
          if (p.meters < 300 && !p.label) {
            setNum += 1;
            p.label = `Set ${setNum} of ${shortAfter.length}`;
          }
        }
      }
    }
  }
  // Position-aware fallback for anything still unlabeled.
  for (let i = 1; i < phases.length; i++) {
    if (phases[i].label) continue;
    if (phases[i].lapEnd - phases[i].lapStart < 3 && phases[i].durationSeconds < 120) {
      phases[i].label = "Re-entry";
    } else {
      phases[i].label = `Block ${phases[i].index}`;
    }
  }
}
